import { Component, inject, OnInit, HostListener } from '@angular/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  BusinessExamInfo,
  BusinessPackageInfo,
  ExamInfo,
  Group,
  GroupInfo,
  PackageDetail,
} from '../../shared/interfaces/reserve-loading.model';
import {
  ComparePlanPackageDetail,
  ComparePlanPackageInfo,
  ComparePlanRequest,
  ComparePlanResponse,
} from '../../shared/interfaces/business-reserve-compare.model';
import { BusinessReserveCompareService } from '../../shared/services/business-reserve-compare.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { NumberToChinesePipe } from '../../pipes/number-to-chinese.pipe';
import { MatDialog } from '@angular/material/dialog';
import { BusinessReserveCompareChooseDialogComponent } from '../../shared/components/dialogs/business-reserve-compare-choose-dialog/business-reserve-compare-choose-dialog.component';
import { Router } from '@angular/router';
import { BusinessReserveChooseGroupDialogComponent } from '../../shared/components/dialogs/business-reserve-choose-group-dialog/business-reserve-choose-group-dialog.component';
import { NumberCommaPipe } from '../../pipes/number-comma.pipe';
import { NgOptimizedImage } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { AddExamInfo } from '../../shared/interfaces/reserve-add.model';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { BusinessAvailableDatesRequest } from '../../shared/interfaces/appointment-time.model';
import { AppointmentTimeService } from '../../shared/services/appointment-time.service';
import { DownloadEnterprisePlanRequest } from '../../shared/interfaces/download-enterprisePlan.model';
import { DownloadEnterprisePlanService } from '../../shared/services/download-enterprisePlan.service';
import { ToastrService } from 'ngx-toastr';
import moment from 'moment';
import { filenameFromContentDisposition } from '../../shared/utils/file-decode.utils';

/**
 * 企業方案比較表組件
 */
@Component({
  selector: 'app-business-reserve-compare',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    NumberCommaPipe,
    NgOptimizedImage,
    MatIconButton,
  ],
  templateUrl: './business-reserve-compare.component.html',
  styleUrl: './business-reserve-compare.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition('* => *', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class BusinessReserveCompareComponent implements OnInit {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  businessReserveCompareService: BusinessReserveCompareService = inject(
    BusinessReserveCompareService
  );
  appointmentTimeService: AppointmentTimeService = inject(AppointmentTimeService);
  translateService: TranslateService = inject(TranslateService);
  dialog: MatDialog = inject(MatDialog);
  downloadEnterprisePlanService: DownloadEnterprisePlanService = inject(DownloadEnterprisePlanService);
  router: Router = inject(Router);
  toastr: ToastrService = inject(ToastrService);

  comparedPackages: Array<ComparePlanPackageDetail | undefined> = [
    undefined,
    undefined,
    undefined,
  ]; // 比較方案列表
  comparePlanRequest: ComparePlanRequest = {
    packageList: [],
  };
  comparePlanResponse: ComparePlanResponse | undefined; // 比較方案結果
  deptNameGroupInfoMap: Map<string, Array<GroupInfo>> = new Map(); // 報告系統別對應底下的groupInfo
  deptNameExamInfoMap: Map<string, Array<BusinessExamInfo>> = new Map(); // 報告系統別對應底下的examInfo
  deptNames: Set<string> = new Set(); // 所有的報告系統別

  numberToChinesePipe: NumberToChinesePipe = new NumberToChinesePipe(); // 數字轉中文pipe

  isDeptNameOpen: Map<string, boolean> = new Map(); // deptName是否展開 { deptName: isOpen }
  isCombinationOpen: boolean = true; // 組合是否展開

  ngOnInit(): void {
    // 將當前預約步驟設置為3
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 3);
    });

    // 設定初始要比較的方案
    const packageDetail = this.sharedStorageService.get(
      'businessSelectedPackage'
    );

    console.log('packageDetail', packageDetail);

    // 設定第一個比較方案
    if (packageDetail) {
      this.comparedPackages[0] = {
        packageNo: packageDetail.packageNo ?? '',
        examItemList: packageDetail.deptList.flatMap(
          (businessPackageInfo: BusinessPackageInfo) => 
            businessPackageInfo.examItemList.map(exam => exam.examItem)
        ) ?? [],
      }
      packageDetail.deptList.forEach((dept: BusinessPackageInfo) => {
        this.isDeptNameOpen.set(dept.deptName, true);
      });
    }
    this.deptNames = new Set(packageDetail?.deptList.map(info => info.deptName) ?? []);
    this.deptNameExamInfoMap = new Map(packageDetail?.deptList.map(info => [info.deptName, info.examItemList]) ?? []);
    this.comparePlanRequest = {
      packageList: [
        {
          packageNo: packageDetail?.packageNo ?? '',
          examItemList: packageDetail?.deptList.flatMap(
            (businessPackageInfo: BusinessPackageInfo) => 
              businessPackageInfo.examItemList.map(exam => exam.examItem)
          ) ?? [],
        },
      ],
    };

    // 設定第二個比較方案
    const secondComparedPackage = this.sharedStorageService.get('secondComparedPackage');
    if (secondComparedPackage) {
      this.comparedPackages[1] = {
        packageNo: secondComparedPackage.packageNo ?? '',
        examItemList: secondComparedPackage.deptList.flatMap(
          (businessPackageInfo: BusinessPackageInfo) => 
            businessPackageInfo.examItemList.map(exam => exam.examItem)
        ) ?? [],
      }
      this.comparePlanRequest.packageList.push(this.comparedPackages[1]);
      
      // 將第二個方案的檢項加入到 deptNameExamInfoMap
      secondComparedPackage.deptList.forEach((dept: BusinessPackageInfo) => {
        this.deptNames.add(dept.deptName);
        const existingExams = this.deptNameExamInfoMap.get(dept.deptName) || [];
        const newExams = dept.examItemList.filter(exam => 
          !existingExams.some(existing => existing.examItem === exam.examItem)
        );
        this.deptNameExamInfoMap.set(dept.deptName, [...existingExams, ...newExams]);
        this.isDeptNameOpen.set(dept.deptName, true);
      });
    }

    // 設定第三個比較方案
    const thirdComparedPackage = this.sharedStorageService.get('thirdComparedPackage');
    if (thirdComparedPackage) {
      this.comparedPackages[2] = {
        packageNo: thirdComparedPackage.packageNo ?? '',
        examItemList: thirdComparedPackage.deptList.flatMap(
          (businessPackageInfo: BusinessPackageInfo) => 
            businessPackageInfo.examItemList.map(exam => exam.examItem)
        ) ?? [],
      }
      this.comparePlanRequest.packageList.push(this.comparedPackages[2]);
      
      // 將第三個方案的檢項加入到 deptNameExamInfoMap
      thirdComparedPackage.deptList.forEach((dept: BusinessPackageInfo) => {
        this.deptNames.add(dept.deptName);
        const existingExams = this.deptNameExamInfoMap.get(dept.deptName) || [];
        const newExams = dept.examItemList.filter(exam => 
          !existingExams.some(existing => existing.examItem === exam.examItem)
        );
        this.deptNameExamInfoMap.set(dept.deptName, [...existingExams, ...newExams]);
        this.isDeptNameOpen.set(dept.deptName, true);
      });
    }
    // this.businessReserveCompareService
    //   .getBusinessPlanComparison(comparePlanRequest)
    //   .then(res => {
    //     const firstComparedPackage = res.data?.packageDetail[0];
    //     if (firstComparedPackage) {
    //       firstComparedPackage.packageNo = packageDetail?.packageNo ?? '';
    //       firstComparedPackage.packageName = packageDetail?.packageName ?? '';
    //       firstComparedPackage.price = packageDetail?.price ?? '';
    //       firstComparedPackage.packageInfo =
    //         packageDetail?.packageList.map(
    //           (businessPackageInfo: BusinessPackageInfo) => {
    //             return this.mapBusinessPackageInfoToComparePlanPackageInfo(
    //               businessPackageInfo
    //             );
    //           }
    //         ) ?? [];
    //     }
    //     this.comparedPackages[0] = firstComparedPackage;
    //   });

    // // 取得檢項，添加到對應的map中
    // const businessPlanResponse = this.sharedStorageService.get(
    //   'businessPlanResponse'
    // );
    // if (businessPlanResponse) {
    //   businessPlanResponse.packageDetail.forEach(
    //     (packageDetail: PackageDetail) => {
    //       packageDetail.packageList.forEach(
    //         (businessPackageInfo: BusinessPackageInfo) => {
    //           this.deptNames.add(businessPackageInfo.deptName);
    //           this.deptNameGroupInfoMap.set(businessPackageInfo.deptName, [
    //             ...(this.deptNameGroupInfoMap.get(
    //               businessPackageInfo.deptName
    //             ) || []),
    //             ...businessPackageInfo.groupInfo.filter(
    //               group =>
    //                 !(
    //                   this.deptNameGroupInfoMap.get(
    //                     businessPackageInfo.deptName
    //                   ) || []
    //                 ).some(
    //                   existingGroup =>
    //                     existingGroup.groupTitleName === group.groupTitleName
    //                 ) // 避免重複添加相同的GroupInfo
    //             ),
    //           ]);
    //           this.deptNameExamInfoMap.set(businessPackageInfo.deptName, [
    //             ...(this.deptNameExamInfoMap.get(
    //               businessPackageInfo.deptName
    //             ) || []),
    //             ...businessPackageInfo.examInfo.filter(
    //               exam =>
    //                 !(
    //                   this.deptNameExamInfoMap.get(
    //                     businessPackageInfo.deptName
    //                   ) || []
    //                 ).some(
    //                   existingExam => existingExam.examItem === exam.examItem
    //                 ) // 避免重複添加相同的ExamInfo
    //             ),
    //           ]);
    //           this.isDeptNameOpen.set(businessPackageInfo.deptName, true); // 初始化deptName的展開狀態為true
    //         }
    //       );
    //     }
    //   );
    // }
  }

  // 第一個比較的方案
  get firstComparedPackage(): ComparePlanPackageDetail | undefined {
    return this.comparedPackages[0];
  }

  // 第二個比較的方案
  get secondComparedPackage(): ComparePlanPackageDetail | undefined {
    return this.comparedPackages[1];
  }

  // 第三個比較的方案
  get thirdComparedPackage(): ComparePlanPackageDetail | undefined {
    return this.comparedPackages[2];
  }

  // 第一個比較的方案的名稱
  get firstComparedPackageDetail(): PackageDetail | undefined {
    const packageDetail = this.sharedStorageService.get('businessSelectedPackage');
    return packageDetail;
  }
  // 第二個比較的方案的名稱
  get secondComparedPackageDetail(): PackageDetail | undefined {
    const packageDetail = this.sharedStorageService.get('secondComparedPackage');
    return packageDetail;
  }
  // 第三個比較的方案的名稱
  get thirdComparedPackageDetail(): PackageDetail | undefined {
    const packageDetail = this.sharedStorageService.get('thirdComparedPackage');
    return packageDetail;
  }

  // examInfo是否存在firstComparedPackage中
  firstComparedPackageContainsExamInfo(examItem: string): boolean {
    return this.comparedPackages[0]?.examItemList.includes(examItem) ?? false;
  }

  // examInfo是否存在secondComparedPackage中
  get secondComparedPackageContainsExamInfo(): (examItem: string) => boolean {
    return (examItem: string): boolean => {
      return this.comparedPackages[1]?.examItemList.includes(examItem) ?? false;
    };
  }

  // examInfo是否存在thirdComparedPackage中
  get thirdComparedPackageContainsExamInfo(): (examItem: string) => boolean {
    return (examItem: string): boolean => {
      return this.comparedPackages[2]?.examItemList.includes(examItem) ?? false;
    };
  }

  // // groupInfo對應firstComparedPackage的文字
  // get firstComparedPackageGroupInfoText(): (groupInfo: GroupInfo) => string {
  //   return (groupInfo: GroupInfo): string => {
  //     if (
  //       !this.firstComparedPackage ||
  //       !this.firstComparedPackage.packageInfo
  //     ) {
  //       return '';
  //     }
      
  //     // 從第一個方案的 packageInfo 中找到對應的 groupInfo
  //     const hasGroupInfo = this.firstComparedPackage.packageInfo.some(info =>
  //       info.groupInfo.some(group => group.groupTitleName === groupInfo.groupTitleName)
  //     );
      
  //     return hasGroupInfo
  //       ? `${this.numberToChinesePipe.transform(groupInfo.groupList.length)}選${this.numberToChinesePipe.transform(groupInfo.chooseCnt)}`
  //       : '';
  //   };
  // }

  // // groupInfo對應secondComparedPackage的文字
  // get secondComparedPackageGroupInfoText(): (groupInfo: GroupInfo) => string {
  //   return (groupInfo: GroupInfo): string => {
  //     if (
  //       !this.secondComparedPackage ||
  //       !this.secondComparedPackage.examItemList
  //     ) {
  //       return '';
  //     }
  //     const groupTitleName = groupInfo.groupTitleName;
  //     const groupTitleNames = this.secondComparedPackage.packageInfo.flatMap(
  //       info => info.groupInfo.map(groupInfo => groupInfo.groupTitleName)
  //     );
  //     return groupTitleNames.includes(groupTitleName)
  //       ? `${this.numberToChinesePipe.transform(groupInfo.groupList.length)}選${this.numberToChinesePipe.transform(groupInfo.chooseCnt)}`
  //       : '';
  //   };
  // }

  // // groupInfo對應thirdComparedPackage的文字
  // get thirdComparedPackageGroupInfoText(): (groupInfo: GroupInfo) => string {
  //   return (groupInfo: GroupInfo): string => {
  //     if (
  //       !this.thirdComparedPackage ||
  //       !this.thirdComparedPackage.packageInfo
  //     ) {
  //       return '';
  //     }
  //     const groupTitleName = groupInfo.groupTitleName;
  //     const groupTitleNames = this.thirdComparedPackage.packageInfo.flatMap(
  //       info => info.groupInfo.map(groupInfo => groupInfo.groupTitleName)
  //     );
  //     return groupTitleNames.includes(groupTitleName)
  //       ? `${this.numberToChinesePipe.transform(groupInfo.groupList.length)}選${this.numberToChinesePipe.transform(groupInfo.chooseCnt)}`
  //       : '';
  //   };
  // }

  // 3種comparedPackage的packageNo
  get comparedPackageNos(): Set<string> {
    return new Set(
      this.comparedPackages
        .filter(
          (pkg): pkg is ComparePlanPackageDetail =>
            pkg !== undefined && pkg.packageNo !== undefined
        )
        .map(pkg => pkg.packageNo!)
    );
  }

  // map BusinessPackageInfo to ComparePlanPackageInfo
  mapBusinessPackageInfoToComparePlanPackageInfo(
    info: BusinessPackageInfo
  ): ComparePlanPackageInfo {
    return {
      deptName: info.deptName,
      deptEName: info.deptEName,
      isGroup: false, // BusinessPackageInfo 沒有此欄位，預設為 false
      groupInfo: [], // BusinessPackageInfo 沒有組合資料，預設為空陣列
      examInfo: info.examItemList.map(exam => ({
        examName: exam.examName,
        examItem: exam.examItem,
      })),
    };
  }

  // map ComparePlanPackageInfo to BusinessPackageInfo
  mapComparePlanPackageInfoToBusinessPackageInfo(
    info: ComparePlanPackageInfo
  ): BusinessPackageInfo {
    return {
      deptName: info.deptName,
      deptEName: info.deptEName,
      examItemList: info.examInfo.map(exam => ({
        examName: exam.examName,
        examItem: exam.examItem,
        examMemo: '', // ComparePlanExamItem 沒有 examMemo，預設為空字串
      })),
    };
  }

  // 顯示檢項dialog
  showExamDialog(examInfo: ExamInfo | AddExamInfo | BusinessExamInfo) {
    const examDialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: examInfo.examName,
        message: examInfo.examMemo,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => examDialog.close(),
      },
    });
  }

  // 顯示組合檢項dialog
  showGroupInfoDialog(groupInfo: GroupInfo) {
    console.log("groupInfo:", groupInfo);
      const message = groupInfo.examMemo;

      const examDialog = this.dialog.open(SingleActionDialogComponent, {
        data: {
          title: groupInfo.examName,
          message: message,
          buttonText: this.translateService.instant('common.button.i-understood'),
          showCloseButton: false,
          action: () => examDialog.close(),
        },
      });
      //  const message = this.translateService.instant(
      // 'business-reserve-compare.group-info-message',
      // {
      //   groups: groupInfo.groupList
      //     .map((group: Group) => group.groupName)
      //     .join('、'),
      //   totalCnt: this.numberToChinesePipe.transform(
      //     groupInfo.groupList.length
      //   ),
      //   chooseCnt: this.numberToChinesePipe.transform(groupInfo.chooseCnt),
      // }
    // );
    // const examDialog = this.dialog.open(SingleActionDialogComponent, {
    //   data: {
    //     title: groupInfo.groupTitleName,
    //     message: message,
    //     buttonText: this.translateService.instant('common.button.i-understood'),
    //     showCloseButton: false,
    //     action: () => examDialog.close(),
    //   },
    // });
  }

  // 替換方案
  replacePackage(index: number) {
    const dialogRef = this.dialog.open(BusinessReserveCompareChooseDialogComponent, {
      panelClass: 'modify-compare-dialog',
      data: {
        excludedPackageNo: this.comparedPackageNos, 
        selectPackageAction: (packageDetail: PackageDetail): void => {
          this.comparedPackages[index] = {
            packageNo: packageDetail.packageNo,
            examItemList: packageDetail.deptList.flatMap(
              (businessPackageInfo: BusinessPackageInfo) => 
                businessPackageInfo.examItemList.map(exam => exam.examItem)
            ) ?? [],
          };
          
          // 更新對應的 SharedStorage
          if (index === 0) {
            this.sharedStorageService.set('businessSelectedPackage', packageDetail);
            this.deptNames = new Set(packageDetail?.deptList.map(info => info.deptName) ?? []);
            this.deptNameExamInfoMap = new Map(packageDetail?.deptList.map(info => [info.deptName, info.examItemList]) ?? []);

            this.isDeptNameOpen.clear();
            packageDetail.deptList.forEach((dept: BusinessPackageInfo) => {
              this.isDeptNameOpen.set(dept.deptName, true);
            });
          } else if (index === 1) {
            this.comparePlanRequest = {
              packageList: [
                // 保留第一個方案
                this.comparePlanRequest.packageList[0],
                // 加入第二個方案
                {
                  packageNo: packageDetail.packageNo,
                  examItemList: packageDetail.deptList.flatMap(
                    (businessPackageInfo: BusinessPackageInfo) => 
                      businessPackageInfo.examItemList.map(exam => exam.examItem)
                  ) ?? [],
                },
                // 如果有第三個方案，也加入進來
                ...(this.comparePlanRequest.packageList[2] ? [this.comparePlanRequest.packageList[2]] : []),
              ]
            }
            console.log('comparePlanRequest', this.comparePlanRequest);
            this.businessReserveCompareService.getBusinessPlanComparison(this.comparePlanRequest).subscribe(res => {
              console.log('res', res);
              if (res.success && res.data) {
                this.comparePlanResponse = res.data;
              }
            });
            this.sharedStorageService.set('secondComparedPackage', packageDetail);
            // 重新建構 deptNameExamInfoMap
            this.rebuildDeptNameExamInfoMap();
          } else if (index === 2) {
            this.comparePlanRequest = {
              packageList: [
                // 保留前兩個方案
                this.comparePlanRequest.packageList[0],
                this.comparePlanRequest.packageList[1],
                // 加入第三個方案
                {
                  packageNo: packageDetail.packageNo,
                  examItemList: packageDetail.deptList.flatMap(
                    (businessPackageInfo: BusinessPackageInfo) => 
                      businessPackageInfo.examItemList.map(exam => exam.examItem)
                  ) ?? [],
                },
              ]
            }
            console.log('comparePlanRequest', this.comparePlanRequest);
            this.businessReserveCompareService.getBusinessPlanComparison(this.comparePlanRequest).subscribe(res => {
              console.log('res', res);
              if (res.success && res.data) {
                this.comparePlanResponse = res.data;
              }
            });
            this.sharedStorageService.set('thirdComparedPackage', packageDetail);

            // 重新建構 deptNameExamInfoMap
            this.rebuildDeptNameExamInfoMap();
          }
        },
      },
    });

    // 監聽 dialog 關閉事件，確保畫面更新
    dialogRef.afterClosed().subscribe(() => {
      // 手動觸發變更檢測
      setTimeout(() => {
        // 使用 setTimeout 確保資料更新後再觸發檢測
      }, 0);
    });
  }

  // 預約方案
  async reservePackage(packageDetail: PackageDetail) {
    // 從所有方案中過濾出選擇的方案，並copy一份新的做為選擇的方案
    const businessPlanResponse = this.sharedStorageService.get(
      'businessPlanResponse'
    );
    packageDetail = JSON.parse(
      JSON.stringify(
        businessPlanResponse?.packageList.filter(
          info => info.packageNo === packageDetail.packageNo
        )[0] ?? packageDetail
      )
    );
    // this.sharedStorageService.set('businessSelectedPackage', packageDetail);

    const packageDetailIncludeGroup = [
      ...(packageDetail.groupChooseList ?? []),
      ...(packageDetail.examChooseList ?? []),
    ];

    console.log('packageDetailIncludeGroup:', packageDetailIncludeGroup);
    let totalGroupCount = 0; // 總共有多少組合

    totalGroupCount = packageDetailIncludeGroup.length;
    
    console.log('totalGroupCount:', totalGroupCount);

    if (totalGroupCount > 0) {
      if (totalGroupCount === 1) {
        // 單一組合，直接處理
        const chooseResult = await this.dialog
          .open(BusinessReserveChooseGroupDialogComponent, {
            data: { 
              group: packageDetailIncludeGroup[0],
              totalGroupCount: totalGroupCount,
            },
          })
          .afterClosed()
          .toPromise();
        
        if (chooseResult) {
          // 過濾selected的group
          const businessSelectedPackage = this.sharedStorageService.get(
            'businessSelectedPackage'
          );
          if (businessSelectedPackage) {
            // 處理 groupChooseList
            businessSelectedPackage.groupChooseList?.forEach(
              (groupChooseInfo: any) => {
                groupChooseInfo.groupList = groupChooseInfo.groupList?.filter(
                  (group: any) => group.selected
                );
              }
            );
            this.sharedStorageService.set(
              'businessSelectedPackage',
              businessSelectedPackage
            );
          }

          this.router.navigate(['/healthcheck/reserve/additem']).then();
        }
      } else {
        console.log("多個組合:", packageDetailIncludeGroup);
        // 多個組合，一次傳入所有組合
        const chooseResult = await this.dialog
          .open(BusinessReserveChooseGroupDialogComponent, {
            data: { 
              groups: packageDetailIncludeGroup,
              totalGroupCount: totalGroupCount,
            },
          })
          .afterClosed()
          .toPromise();
        
        if (chooseResult) {
          // 過濾selected的group
          const businessSelectedPackage = this.sharedStorageService.get(
            'businessSelectedPackage'
          );
          if (businessSelectedPackage) {
            // 處理 groupChooseList
            businessSelectedPackage.groupChooseList?.forEach(
              (groupChooseInfo: any) => {
                groupChooseInfo.groupList = groupChooseInfo.groupList?.filter(
                  (group: any) => group.selected
                );
              }
            );
            this.sharedStorageService.set(
              'businessSelectedPackage',
              businessSelectedPackage
            );
          }
          this.router.navigate(['/healthcheck/reserve/additem']).then();
        }
      }
    } else {
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
      console.log('businessSelectedPackage', businessSelectedPackage);
      if (!businessPlanRequest || !businessSelectedPackage) return;
      // 更新選擇狀態後，再收集檢項
      const request: BusinessAvailableDatesRequest = {
        orgNo: businessPlanRequest?.orgNo ?? '',
        companyNo: businessPlanRequest?.companyNo ?? '',
        packageNo: businessSelectedPackage.packageNo ?? '',
        gender: businessPlanRequest?.gender ?? '',
        dayType: businessSelectedPackage.dayType ?? '',
        bookPeriod: businessSelectedPackage.bookPeriod ?? '',
        examItemList: this.buildSimpleExamItemList(),
        addList: [],
      };
      
      // 查詢有無可預約時間
      this.appointmentTimeService.getBusinessAvailableDates(request).subscribe(res => {
        console.log('res', res);
        if (res.data && res.data.bookCalInfos && res.data.nutCalInfos) {
          const allBookCntZero = res.data.bookCalInfos.every(info => Number(info.bookCnt) === 0);
          const allNutCntZero = res.data.nutCalInfos.every(info => Number(info.nutBookCnt) === 0);
          console.log('allBookCntZero', allBookCntZero);
          console.log('allNutCntZero', allNutCntZero);

          // 若沒有可預約人數
          if (allBookCntZero && allNutCntZero) {
            const dialog = this.dialog.open(SingleActionDialogComponent, {
              data: {
                title: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-title'),
                message: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-message'),
                buttonText: this.translateService.instant('business-reserve-compare-choose-dialog.dialog-reselect'),
                showCloseButton: false,
                action: () => {
                  this.sharedStorageService.remove('businessSelectedPackage');
                  this.sharedStorageService.remove('secondComparedPackage');
                  this.sharedStorageService.remove('thirdComparedPackage');
                  dialog.close();
                  this.router.navigate(['/healthcheck/reserve/companyplan']).then();
                },
              },
            });
          } else {
            // 建立完整的檢項清單並儲存到 sharedStorage
            this.buildAndSaveExamItemList();

            this.router.navigate(['/healthcheck/reserve/additem']).then();
          }
        }
      });

    }
  }

  // 重選方案
  reSelectPackage() {
    this.sharedStorageService.remove('examItemList');
    this.sharedStorageService.remove('thirdComparedPackage');
    this.sharedStorageService.remove('secondComparedPackage');
    this.sharedStorageService.remove('businessSelectedPackage');
    this.router.navigate(['/healthcheck/reserve/companyplan']).then();
  }

  //滾動對應差異方案 出現固定方案
  currentTableHeaderTitle: string = '';
  isTableFixedActive: boolean = false;
  animationKey = 0;

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const reserveTableBlock = document.querySelector(
      '.reserve-table-block'
    ) as HTMLElement;
    const tableHeaders = this.getTableHeaders();
    let activeTitle = '';

    if (reserveTableBlock) {
      const blockPosition = reserveTableBlock.getBoundingClientRect();
      const offset = window.innerWidth <= 768 ? 215 : 88;
      this.isTableFixedActive =
        blockPosition.top <= offset && blockPosition.bottom > offset;
    }

    tableHeaders.forEach((header: HTMLElement) => {
      const headerPosition = header.getBoundingClientRect();
      const offset = window.innerWidth <= 768 ? 189 : 83;
      if (headerPosition.top <= offset && headerPosition.bottom > offset) {
        const titleElement = header.querySelector(
          '.plan-table-header-title'
        ) as HTMLElement;
        activeTitle = titleElement?.textContent?.trim() || '';
      }
    });

    // 加 key 值来觸發動畫
    if (this.currentTableHeaderTitle !== activeTitle) {
      this.currentTableHeaderTitle = activeTitle;
      this.animationKey++; //
    }
  }

  // 檢查是否有第二個比較方案
  get hasSecondComparedPackage(): boolean {
    return this.secondComparedPackage !== undefined;
  }

  private getTableHeaders(): HTMLElement[] {
    return Array.from(
      document.querySelectorAll('.plan-table')
    ) as HTMLElement[];
  }

  // 取得所有組合選項
  get allGroupChooseList(): any[] {
    const allGroups: any[] = [];
    const packages = [this.firstComparedPackageDetail, this.secondComparedPackageDetail, this.thirdComparedPackageDetail];
    
    packages.forEach(pkg => {
      if (pkg?.groupChooseList) {
        pkg.groupChooseList.forEach((group: any) => {
          if (!allGroups.some(g => g.groupTitleName === group.groupTitleName)) {
            allGroups.push(group);
          }
        });
      }
    });
    
    return allGroups;
  }

  // 取得所有檢項選擇
  get allExamChooseList(): any[] {
    const allExams: any[] = [];
    const packages = [this.firstComparedPackageDetail, this.secondComparedPackageDetail, this.thirdComparedPackageDetail];
    
    packages.forEach(pkg => {
      if (pkg?.examChooseList) {
        pkg.examChooseList.forEach((exam: any) => {
          if (!allExams.some(e => e.groupTitleName === exam.groupTitleName)) {
            allExams.push(exam);
          }
        });
      }
    });
    
    return allExams;
  }

  // 取得方案的組合選擇文字
  getPackageGroupChooseText(packageIndex: number, groupChoose: any): string {
    const packages = [this.firstComparedPackageDetail, this.secondComparedPackageDetail, this.thirdComparedPackageDetail];
    const pkg = packages[packageIndex];
    
    if (!pkg?.groupChooseList) return '';
    
    // 取得目標組合的檢項組合
    const targetExamItems = groupChoose.groupList?.map((item: any) => 
      item.examItemList?.map((exam: any) => exam.examItem).sort().join(',')
    ).sort().join('|') || '';
    
    // 在該方案中尋找相同檢項組合的組合選擇
    const found = pkg.groupChooseList.find((g: any) => {
      const currentExamItems = g.groupList?.map((item: any) => 
        item.examItemList?.map((exam: any) => exam.examItem).sort().join(',')
      ).sort().join('|') || '';
      return currentExamItems === targetExamItems;
    });
    
    if (!found) return '';
    
    const totalText = this.numberToChinesePipe.transform(found.groupList?.length || 0);
    const chooseText = this.numberToChinesePipe.transform(found.chooseCount || 0);
    
    return `${totalText}選${chooseText}`;
  }

  // 取得方案的檢項選擇文字
  getPackageExamChooseText(packageIndex: number, examChoose: any): string {
    const packages = [this.firstComparedPackageDetail, this.secondComparedPackageDetail, this.thirdComparedPackageDetail];
    const pkg = packages[packageIndex];
    
    if (!pkg?.examChooseList) return '';
    
    // 取得目標檢項組合
    const targetExamItems = examChoose.examItemList?.map((exam: any) => exam.examItem).sort().join(',') || '';
    
    // 在該方案中尋找相同檢項組合的檢項選擇
    const found = pkg.examChooseList.find((e: any) => {
      const currentExamItems = e.examItemList?.map((exam: any) => exam.examItem).sort().join(',') || '';
      return currentExamItems === targetExamItems;
    });
    
    if (!found) return '';
    
    const totalText = this.numberToChinesePipe.transform(found.examItemList?.length || 0);
    const chooseText = this.numberToChinesePipe.transform(found.chooseCount || 0);
    
    return `${totalText}選${chooseText}`;
  }

  // 顯示選擇資訊dialog（用於 groupChooseList 和 examChooseList）
  showChooseInfoDialog(chooseInfo: any) {
    let itemNames: string[] = [];
    let message = '';
    
    if (chooseInfo.groupList) {
      // GroupChooseInfo 類型
      itemNames = chooseInfo.groupList.map((group: any) => group.groupName || group.examName);
    } else if (chooseInfo.examItemList) {
      // ExamChooseInfo 類型
      itemNames = chooseInfo.examItemList.map((exam: any) => exam.examName);
    }
    
    const totalCnt = this.numberToChinesePipe.transform(itemNames.length);
    const chooseCnt = this.numberToChinesePipe.transform(chooseInfo.chooseCount);
    
    message = `從以下 ${totalCnt} 個選項中選擇 ${chooseCnt} 個：\n${itemNames.join('、')}`;
    
    const dialog = this.dialog.open(SingleActionDialogComponent, {
      data: {
        title: chooseInfo.groupTitleName,
        message: message,
        buttonText: this.translateService.instant('common.button.i-understood'),
        showCloseButton: false,
        action: () => dialog.close(),
      },
    });
  }

  // 取得按方案順序排列的組合項目（去除重複組合）
  get sortedCombinationItemsByPackage() {
    const items: any[] = [];
    const seenGroups = new Set<string>(); // 追蹤已顯示的組合
    const packages = [this.firstComparedPackageDetail, this.secondComparedPackageDetail, this.thirdComparedPackageDetail];
    
    packages.forEach((pkg, packageIndex) => {
      if (!pkg) return;
      
      // 處理該方案的 groupChooseList
      pkg.groupChooseList?.forEach((groupChoose: any, groupIndex: number) => {
        groupChoose.groupList?.forEach((groupItem: any, itemIndex: number) => {
          // 檢查這個組合項目的所有檢項是否已經存在
          const examItems = groupItem.examItemList?.map((exam: any) => exam.examItem).sort().join(',') || '';
          const uniqueKey = `group-${examItems}`;
          
          if (!seenGroups.has(uniqueKey)) {
            seenGroups.add(uniqueKey);
            
            items.push({
              type: 'group',
              packageIndex,
              data: groupItem,
              parentData: groupChoose,
              groupIndex,
              itemIndex,
              sortOrder: packageIndex * 10000 + groupIndex * 100 + itemIndex
            });
          }
        });
      });
      
      // 處理該方案的 examChooseList
      pkg.examChooseList?.forEach((examChoose: any, examIndex: number) => {
        // 檢查這個檢項選擇的所有檢項是否已經存在
        const examItems = examChoose.examItemList?.map((exam: any) => exam.examItem).sort().join(',') || '';
        const uniqueKey = `exam-${examItems}`;
        
        if (!seenGroups.has(uniqueKey)) {
          seenGroups.add(uniqueKey);
          
          examChoose.examItemList?.forEach((examItem: any, itemIndex: number) => {
            items.push({
              type: 'exam',
              packageIndex,
              data: examItem,
              parentData: examChoose,
              examIndex,
              itemIndex,
              sortOrder: packageIndex * 10000 + (pkg.groupChooseList?.length || 0) * 100 + examIndex * 100 + itemIndex
            });
          });
        }
      });
    });
    
    return items.sort((a, b) => a.sortOrder - b.sortOrder);
  }
  // 建立簡化的檢項清單，只包含 examItem 和 examName
  private buildSimpleExamItemList(): { examItem: string; examName: string }[] {
    const examItemList: { examItem: string; examName: string }[] = [];
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    
    if (!businessSelectedPackage) return examItemList;

    // 加入方案的 deptList 檢項
    businessSelectedPackage.deptList?.forEach((dept: any) => {
      dept.examItemList?.forEach((exam: any) => {
        examItemList.push({
          examItem: exam.examItem,
          examName: exam.examName
        });
      });
    });
    return examItemList;
  }

  // 建立完整的檢項清單並儲存到 sharedStorage
  private buildAndSaveExamItemList(): void {
    const examItemList: any[] = [];
    const businessSelectedPackage = this.sharedStorageService.get('businessSelectedPackage');
    
    if (!businessSelectedPackage) return;

    // 加入方案的 deptList 檢項
    businessSelectedPackage.deptList?.forEach((dept: any) => {
      dept.examItemList?.forEach((exam: any) => {
        examItemList.push({
          deptName: dept.deptName,
          examName: exam.examName,
          examItem: exam.examItem,
          examMemo: exam.examMemo || '',
          examPrice: exam.examPrice || '0',
          selectFlag: 1
        });
      });
    });
    console.log('examItemList', examItemList);

    this.sharedStorageService.set('examItemList', examItemList);
  }

  // 重新建構 deptNameExamInfoMap
  private rebuildDeptNameExamInfoMap() {
    this.deptNameExamInfoMap.clear();

    // 重新整合第一個方案
    const firstPackage = this.sharedStorageService.get('businessSelectedPackage');
    if (firstPackage) {
      firstPackage.deptList.forEach((dept: BusinessPackageInfo) => {
        this.deptNames.add(dept.deptName);
        this.deptNameExamInfoMap.set(dept.deptName, [...dept.examItemList]);
        this.isDeptNameOpen.set(dept.deptName, true);
      });
    }
    
    // 重新整合第二個方案
    const secondPackage = this.sharedStorageService.get('secondComparedPackage');
    if (secondPackage) {
      secondPackage.deptList.forEach((dept: BusinessPackageInfo) => {
        this.deptNames.add(dept.deptName);
        const existingExams = this.deptNameExamInfoMap.get(dept.deptName) || [];
        const newExams = dept.examItemList.filter(exam => 
          !existingExams.some(existing => existing.examItem === exam.examItem)
        );
        this.deptNameExamInfoMap.set(dept.deptName, [...existingExams, ...newExams]);
        this.isDeptNameOpen.set(dept.deptName, true);
      });
    }
    
    // 重新整合第三個方案
    const thirdPackage = this.sharedStorageService.get('thirdComparedPackage');
    if (thirdPackage) {
      thirdPackage.deptList.forEach((dept: BusinessPackageInfo) => {
        this.deptNames.add(dept.deptName);
        const existingExams = this.deptNameExamInfoMap.get(dept.deptName) || [];
        const newExams = dept.examItemList.filter(exam => 
          !existingExams.some(existing => existing.examItem === exam.examItem)
        );
        this.deptNameExamInfoMap.set(dept.deptName, [...existingExams, ...newExams]);
        this.isDeptNameOpen.set(dept.deptName, true);
      });
    }
  }

  // 下載企業方案比較表PDF
  downloadEnterprisePlan() {
    console.log('下載企業方案比較表');
    const request: DownloadEnterprisePlanRequest = {
      orgNo: this.sharedStorageService.get('businessPlanRequest')?.orgNo ?? '',
      companyNo: this.sharedStorageService.get('businessPlanRequest')?.companyNo ?? '',
      gender: this.sharedStorageService.get('businessPlanRequest')?.gender ?? '',
      packageNoList: this.comparedPackages.map(pkg => pkg?.packageNo ?? '').filter(Boolean),
      idNo: this.sharedStorageService.get('userPlanRequest')?.idNo ?? '',
      employeeId: this.sharedStorageService.get('businessPlanRequest')?.employeeId ?? '',
    }
    this.downloadEnterprisePlanService.downloadEnterprisePlan(request).subscribe({
      next: (resp) => {
        const blob = resp.body as Blob;
        const cd = resp.headers.get('content-disposition');
        
        // 解析檔名
        let fileName = filenameFromContentDisposition(cd);
        if (!fileName) {
          const orgNo = this.sharedStorageService.get('businessPlanRequest')?.orgNo ?? '';
          const centerName = this.centerMapping[orgNo] || orgNo;
          fileName = `${moment().format('YYYYMMDD')}-健檢方案比較表-${centerName}中心.pdf`;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: async (err) => {
        console.error('下載 API 發生錯誤:', err);
        
        // 將 Blob 轉換為 JSON 以取得 errorMessage
        let errorMessage = '下載失敗,請稍後再試';
        if (err.error instanceof Blob) {
          try {
            const text = await err.error.text();
            const errorObj = JSON.parse(text);
            errorMessage = errorObj.errorMessage || errorMessage;
          } catch (parseError) {
            console.error('解析錯誤訊息失敗:', parseError);
          }
        } else if (err.error?.errorMessage) {
          errorMessage = err.error.errorMessage;
        }
        
        this.toastr.error(errorMessage);
      }
    });
  }

  // 據點轉換
  private centerMapping: { [key: string]: string } = {
    '01': '敦南',
    '02': '內湖',
    '04': '新竹',
    '05': '台南',
  };
}
